import React, { useState } from "react";
import { JOBS, CANDIDATES, APPLICATIONS, KANBAN_STAGES, PLANS, SKILLS_LIB, CITIES } from "./data";
import { AvailBadge, Chip, Icon, MatchRing, Logo, Avatar, Progress } from "./ui";

export default function EmployerApp({ onExit }) {
  const [screen, setScreen] = useState("login");
  const [nav, setNav] = useState("dashboard");
  const [applications, setApplications] = useState(APPLICATIONS);
  const [showPost, setShowPost] = useState(false);
  const [showRazorpay, setShowRazorpay] = useState(null);
  const [showUnlock, setShowUnlock] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const moveStage = (id, stage) => setApplications(applications.map(a => a.id === id ? { ...a, stage } : a));

  return (
    <div className="min-h-screen grain relative" style={{background: "var(--paper)"}}>
      {screen === "login" && <LoginPage go={() => setScreen("verify")} onExit={onExit}/>}
      {screen === "verify" && <VerifyPage go={() => setScreen("plans")}/>}
      {screen === "plans" && <PlansPage onExit={onExit} onSelect={(plan) => setShowRazorpay(plan)} onSkip={() => setScreen("app")}/>}
      {screen === "app" && (
        <div className="flex min-h-screen relative z-10">
          <Sidebar nav={nav} setNav={setNav} onExit={onExit} open={sidebarOpen} setOpen={setSidebarOpen}/>
          <main className="flex-1 max-w-full lg:ml-0 min-w-0">
            <TopBar onMenu={() => setSidebarOpen(true)}/>
            <div className="p-4 md:p-8 max-w-7xl">
              {nav === "dashboard" && <Dashboard applications={applications} onPost={() => setShowPost(true)}/>}
              {nav === "jobs" && <JobsPage onPost={() => setShowPost(true)}/>}
              {nav === "candidates" && <CandidatesPage onUnlock={setShowUnlock}/>}
              {nav === "applications" && <KanbanBoard applications={applications} onMove={moveStage}/>}
              {nav === "analytics" && <Analytics/>}
              {nav === "billing" && <Billing onUpgrade={(p) => setShowRazorpay(p)}/>}
              {nav === "team" && <TeamPage/>}
            </div>
          </main>
        </div>
      )}
      {showPost && <PostJobModal onClose={() => setShowPost(false)}/>}
      {showRazorpay && <RazorpayModal plan={showRazorpay} onClose={() => setShowRazorpay(null)} onSuccess={() => { setShowRazorpay(null); setScreen("app"); }}/>}
      {showUnlock && <UnlockModal candidate={showUnlock} onClose={() => setShowUnlock(null)}/>}
    </div>
  );
}

// ---------------- Auth / Onboarding ----------------
function LoginPage({ go, onExit }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10">
      <div className="p-10 lg:p-16 flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <Logo/>
          <button onClick={onExit} className="btn btn-ghost text-sm">All prototypes</button>
        </div>
        <div className="max-w-md">
          <p className="text-xs uppercase tracking-widest text-[var(--muted)] mb-3">Employer sign in</p>
          <h1 className="font-display text-5xl leading-[1.02]">Hire people who can<br/><span className="text-[var(--brand-2)]">actually join</span> in weeks.</h1>
          <p className="text-[var(--ink-2)] mt-4">TalentXpres matches your urgent openings only with candidates whose availability fits your timeline.</p>
          <div className="mt-8 space-y-3 max-w-sm">
            <input className="w-full px-4 py-3 rounded-xl border border-[var(--line)] bg-white" defaultValue="priya@acmecorp.in"/>
            <input type="password" className="w-full px-4 py-3 rounded-xl border border-[var(--line)] bg-white" defaultValue="••••••••"/>
            <button data-testid="employer-login-btn" onClick={go} className="btn btn-primary w-full">Sign in</button>
            <button className="btn btn-ghost w-full">Continue with Google</button>
          </div>
          <p className="text-sm text-[var(--muted)] mt-6">New here? <a className="text-[var(--brand)] font-semibold cursor-pointer" onClick={go}>Create a company account</a></p>
        </div>
        <div className="text-xs text-[var(--muted)]">© 2026 TalentXpres · Mumbai · Bangalore</div>
      </div>
      <div className="hidden lg:block relative overflow-hidden" style={{background: "linear-gradient(135deg, #0F3D2E 0%, #082018 100%)"}}>
        <div className="absolute inset-0 grain opacity-60"/>
        <div className="p-16 text-white h-full flex flex-col justify-between relative">
          <div className="space-y-4">
            <div className="text-xs uppercase tracking-widest text-white/60">Live now</div>
            {[
              {name: "Meera Nair", role: "Data Scientist", days: 12, tone: "green"},
              {name: "Sneha Rao", role: "Product Designer", days: 0, tone: "green"},
              {name: "Arjun Mehta", role: "Backend Engineer", days: 35, tone: "yellow"},
            ].map((c, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/5 backdrop-blur border border-white/10 flex justify-between items-center">
                <div>
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-white/60">{c.role}</div>
                </div>
                <AvailBadge status={c.days === 0 ? "immediate" : "notice"} days={c.days}/>
              </div>
            ))}
          </div>
          <blockquote className="text-2xl font-display leading-snug border-l-2 border-[var(--brand-2)] pl-6">
            {`"Filled 4 senior roles in 3 weeks. TalentXpres is the only platform where notice period is the first filter."`}
            <div className="text-sm text-white/60 mt-3 font-sans not-italic">— Head of Talent, Series B fintech</div>
          </blockquote>
        </div>
      </div>
    </div>
  );
}

function VerifyPage({ go }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 relative z-10">
      <div className="max-w-2xl w-full">
        <p className="text-xs uppercase tracking-widest text-[var(--muted)]">Step 1 of 3 · Company setup</p>
        <h1 className="font-display text-4xl mt-3">Verify your company</h1>
        <p className="text-[var(--ink-2)] mt-2">We manually verify every employer for candidate trust.</p>

        <div className="mt-8 card p-6 grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-[var(--muted)]">Company name *</label>
            <input defaultValue="AcmeCorp Technologies" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white"/>
          </div>
          <div>
            <label className="text-xs text-[var(--muted)]">Website</label>
            <input defaultValue="https://acmecorp.in" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white"/>
          </div>
          <div>
            <label className="text-xs text-[var(--muted)]">GST / CIN *</label>
            <input defaultValue="U72900MH2020PTC123456" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white"/>
          </div>
          <div>
            <label className="text-xs text-[var(--muted)]">Industry</label>
            <select className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white"><option>SaaS</option><option>Fintech</option><option>Healthcare</option></select>
          </div>
          <div>
            <label className="text-xs text-[var(--muted)]">Size</label>
            <select className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white"><option>51-200</option></select>
          </div>
          <div className="md:col-span-2 border border-dashed border-[var(--line)] rounded-xl p-4 text-center bg-white">
            <div className="text-sm font-medium">Upload company logo</div>
            <div className="text-xs text-[var(--muted)] mt-1">PNG/SVG · 512×512 recommended</div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-6">
          <div className="text-xs text-[var(--muted)]">Manual approval usually within 2 business hours.</div>
          <button data-testid="verify-continue" onClick={go} className="btn btn-primary">Submit & continue</button>
        </div>
      </div>
    </div>
  );
}

function PlansPage({ onSelect, onSkip, onExit }) {
  return (
    <div className="min-h-screen p-8 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest text-[var(--muted)]">Step 2 of 3 · Choose plan</p>
          <button onClick={onSkip} className="text-sm text-[var(--muted)] underline">Skip for now</button>
        </div>
        <h1 className="font-display text-4xl mt-3">Pick a plan to start hiring</h1>
        <p className="text-[var(--ink-2)] mt-2">All plans include the availability-first matching engine.</p>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {PLANS.map(p => (
            <div key={p.key} className={`card p-6 relative ${p.highlight ? "border-[var(--brand-2)] shadow-lg" : ""}`}>
              {p.highlight && <div className="absolute -top-3 left-6"><Chip tone="mint">14-day free trial</Chip></div>}
              <div className="font-display text-2xl">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-4xl">₹{p.price.toLocaleString("en-IN")}</span>
                <span className="text-sm text-[var(--muted)]">{p.period}</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map(f => <li key={f} className="flex gap-2"><Icon.Check color="var(--brand-2)"/> {f}</li>)}
              </ul>
              <button data-testid={`plan-${p.key}`} onClick={() => onSelect(p)} className={`btn w-full mt-6 ${p.highlight ? "btn-primary" : "btn-ghost"}`}>{p.cta}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Layout ----------------
function Sidebar({ nav, setNav, onExit, open, setOpen }) {
  const items = [
    { key: "dashboard", label: "Dashboard", icon: <Icon.Home/> },
    { key: "jobs", label: "Jobs", icon: <Icon.Briefcase/> },
    { key: "candidates", label: "Candidates", icon: <Icon.Users/> },
    { key: "applications", label: "Kanban", icon: <Icon.Grid/> },
    { key: "analytics", label: "Analytics", icon: <Icon.Chart/> },
    { key: "team", label: "Team", icon: <Icon.User/> },
    { key: "billing", label: "Billing", icon: <Icon.Money/> },
  ];
  return (
    <>
      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)}/>}
      <aside className={`w-64 border-r border-[var(--line)] bg-white flex flex-col p-4 z-40 transition-transform fixed inset-y-0 left-0 lg:sticky lg:top-0 lg:translate-x-0 lg:h-screen ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{minHeight: "100vh"}}>
        <div className="px-2 py-2"><Logo/></div>
        <div className="text-xs text-[var(--muted)] px-3 mt-1">Employer console</div>
        <nav className="mt-6 space-y-1 flex-1">
          {items.map(i => (
            <div key={i.key} data-testid={`nav-${i.key}`} className={`side-item ${nav === i.key ? "active" : ""}`} onClick={() => { setNav(i.key); setOpen(false); }}>
              {i.icon}{i.label}
            </div>
          ))}
        </nav>
        <button onClick={onExit} className="btn btn-ghost text-xs">← All prototypes</button>
      </aside>
    </>
  );
}

function TopBar({ onMenu }) {
  return (
    <div className="border-b border-[var(--line)] bg-white px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
      <div className="flex items-center gap-3">
        <button onClick={onMenu} className="lg:hidden p-2 -ml-2" aria-label="Menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
        </button>
        <div className="font-display text-base md:text-lg truncate">AcmeCorp Technologies</div>
        <Chip tone="mint">Pro · 14d</Chip>
      </div>
      <div className="flex items-center gap-3 md:gap-4">
        <div className="relative"><Icon.Bell/><span className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--accent)] rounded-full"/></div>
        <Avatar name="Priya Sharma" size={36}/>
      </div>
    </div>
  );
}

// ---------------- Dashboard ----------------
function Dashboard({ applications, onPost }) {
  const stats = [
    { label: "Active jobs", value: 4, hint: "+1 this week" },
    { label: "New applications", value: applications.filter(a => a.stage === "new").length, hint: "in last 24h" },
    { label: "In interview", value: applications.filter(a => a.stage === "interview").length, hint: "5 avg" },
    { label: "Avg. time to hire", value: "19d", hint: "vs 42d industry" },
  ];
  return (
    <div className="fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl">Good morning, Priya.</h1>
          <p className="text-[var(--ink-2)] mt-1">3 candidates match your urgent DevOps role and can join before your deadline.</p>
        </div>
        <button data-testid="dashboard-post-job" onClick={onPost} className="btn btn-accent"><Icon.Plus/> Post a job</button>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(s => (
          <div key={s.label} className="card p-5">
            <div className="text-xs text-[var(--muted)]">{s.label}</div>
            <div className="font-display text-3xl mt-1">{s.value}</div>
            <div className="text-xs text-[var(--brand)] mt-1">{s.hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-4 md:p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl">Application funnel — last 30 days</h3>
            <Chip>All jobs</Chip>
          </div>
          <div className="mt-6 space-y-3">
            {[
              { l: "Applied", v: 128, w: 100 },
              { l: "Screened", v: 62, w: 48 },
              { l: "Shortlisted", v: 34, w: 27 },
              { l: "Interview", v: 18, w: 14 },
              { l: "Offer", v: 6, w: 5 },
              { l: "Hired", v: 3, w: 2 },
            ].map(r => (
              <div key={r.l} className="flex items-center gap-3">
                <div className="w-24 text-sm text-[var(--ink-2)]">{r.l}</div>
                <div className="flex-1 h-6 bg-[var(--paper-2)] rounded-md overflow-hidden">
                  <div className="h-full bg-[var(--brand)] rounded-md" style={{ width: `${r.w}%` }}/>
                </div>
                <div className="w-10 text-right text-sm font-semibold">{r.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-6">
          <h3 className="font-display text-xl">Urgent matches today</h3>
          <div className="mt-4 space-y-3">
            {CANDIDATES.slice(0, 3).map(c => (
              <div key={c.id} className="flex items-center gap-3">
                <Avatar name={c.name} size={40}/>
                <div className="flex-1">
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="text-xs text-[var(--muted)]">{c.title}</div>
                </div>
                <AvailBadge status={c.availability.status} days={c.availability.days}/>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- Jobs ----------------
function JobsPage({ onPost }) {
  return (
    <div className="fade-up">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Jobs</h1>
        <button data-testid="jobs-post" onClick={onPost} className="btn btn-accent"><Icon.Plus/> Post a job</button>
      </div>
      <div className="mt-8 card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[var(--paper-2)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
            <tr><th className="px-6 py-3">Role</th><th className="px-6 py-3">Location</th><th className="px-6 py-3">Max joining</th><th className="px-6 py-3">Applicants</th><th className="px-6 py-3">Posted</th><th className="px-6 py-3">Status</th></tr>
          </thead>
          <tbody>
            {JOBS.map(j => (
              <tr key={j.id} className="border-t border-[var(--line)] hover:bg-[var(--paper-2)]/50">
                <td className="px-6 py-4 font-medium">{j.title}</td>
                <td className="px-6 py-4 text-[var(--ink-2)]">{j.location} · {j.workMode}</td>
                <td className="px-6 py-4"><Chip tone="accent">≤{j.maxJoiningDays} days</Chip></td>
                <td className="px-6 py-4">{j.applicants}</td>
                <td className="px-6 py-4 text-[var(--muted)]">{j.postedAgo}</td>
                <td className="px-6 py-4"><Chip tone="mint">Active</Chip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Candidates ----------------
function CandidatesPage({ onUnlock }) {
  return (
    <div className="fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl">Candidate discovery</h1>
          <p className="text-[var(--ink-2)] mt-1">Sorted by availability — soonest to join first.</p>
        </div>
        <div className="text-sm">Credits: <b>47</b> / 50</div>
      </div>

      <div className="mt-6 card p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--line)] bg-white min-w-[220px]">
          <Icon.Search/><input placeholder="Skills, role, location" className="flex-1 outline-none text-sm bg-transparent"/>
        </div>
        <select className="px-3 py-2 rounded-lg border border-[var(--line)] bg-white text-sm">
          <option>Any joining timeline</option>
          <option>Immediate (0 days)</option>
          <option>≤ 15 days</option>
          <option>≤ 30 days</option>
          <option>≤ 60 days</option>
        </select>
        <select className="px-3 py-2 rounded-lg border border-[var(--line)] bg-white text-sm">
          <option>Any experience</option><option>0-2 yrs</option><option>3-5 yrs</option><option>6+ yrs</option>
        </select>
        <button className="btn btn-ghost text-sm"><Icon.Filter/> More filters</button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {CANDIDATES.map(c => (
          <div key={c.id} className="card p-5 flex gap-4">
            <Avatar name={c.name} size={56}/>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg">{c.name}</div>
                  <div className="text-sm text-[var(--muted)]">{c.title} · {c.company}</div>
                </div>
                <AvailBadge status={c.availability.status} days={c.availability.days}/>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.skills.slice(0, 4).map(s => <Chip key={s} tone="mint">{s}</Chip>)}
                {c.skills.length > 4 && <Chip>+{c.skills.length-4}</Chip>}
              </div>
              <div className="mt-3 flex gap-4 text-xs text-[var(--ink-2)]">
                <span><Icon.Location/> {c.location}</span>
                <span>{c.exp} yrs exp</span>
                <span>Expects ₹{c.salary}L</span>
              </div>
              <div className="mt-4 flex gap-2">
                <button data-testid={`unlock-${c.id}`} onClick={() => onUnlock(c)} className="btn btn-primary text-xs px-3 py-1.5">Unlock profile (1 credit)</button>
                <button className="btn btn-ghost text-xs px-3 py-1.5"><Icon.Bookmark/> Shortlist</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Kanban ----------------
function KanbanBoard({ applications, onMove }) {
  const [dragged, setDragged] = useState(null);
  return (
    <div className="fade-up">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Applications</h1>
        <div className="flex gap-2">
          <select className="px-3 py-2 rounded-lg border border-[var(--line)] bg-white text-sm">
            <option>All jobs</option>
            {JOBS.map(j => <option key={j.id}>{j.title}</option>)}
          </select>
        </div>
      </div>
      <p className="text-[var(--ink-2)] mt-1">Drag candidates through the pipeline. Availability badge is always visible.</p>

      <div className="mt-6 flex gap-3 overflow-x-auto pb-4">
        {KANBAN_STAGES.map(col => {
          const items = applications.filter(a => a.stage === col.key);
          return (
            <div key={col.key} className="kanban-col flex-shrink-0" style={{width: 280}}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragged) onMove(dragged, col.key); setDragged(null); }}
              data-testid={`kanban-col-${col.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold">{col.label}</div>
                <Chip>{items.length}</Chip>
              </div>
              {items.map(a => {
                const c = CANDIDATES.find(x => x.id === a.candidateId);
                const j = JOBS.find(x => x.id === a.jobId);
                if (!c) return null;
                return (
                  <div key={a.id} className="kanban-card" draggable onDragStart={() => setDragged(a.id)}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex gap-2">
                        <Avatar name={c.name} size={32}/>
                        <div>
                          <div className="font-medium text-sm">{c.name}</div>
                          <div className="text-xs text-[var(--muted)]">{c.title}</div>
                        </div>
                      </div>
                      <MatchRing score={a.match} size={32}/>
                    </div>
                    <div className="mt-2 text-xs text-[var(--muted)]">→ {j.title}</div>
                    <div className="mt-2"><AvailBadge status={c.availability.status} days={c.availability.days}/></div>
                    <div className="mt-2 flex justify-between text-xs text-[var(--muted)]"><span>{a.appliedAgo}</span><span>#{a.id.toUpperCase()}</span></div>
                  </div>
                );
              })}
              {items.length === 0 && <div className="text-xs text-[var(--muted)] text-center py-6">Drop candidates here</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Analytics ----------------
function Analytics() {
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Analytics</h1>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {l:"Jobs posted", v:12}, {l:"Total apps", v:284}, {l:"Interview→Offer", v:"38%"}, {l:"Time to hire", v:"19d"},
        ].map(x => (
          <div key={x.l} className="card p-5"><div className="text-xs text-[var(--muted)]">{x.l}</div><div className="font-display text-3xl mt-1">{x.v}</div></div>
        ))}
      </div>
      <div className="mt-6 card p-6">
        <h3 className="font-display text-xl mb-4">Applications by role</h3>
        <div className="space-y-3">
          {JOBS.map(j => (
            <div key={j.id} className="flex items-center gap-3">
              <div className="w-56 text-sm text-[var(--ink-2)] truncate">{j.title}</div>
              <div className="flex-1 h-5 bg-[var(--paper-2)] rounded-md">
                <div className="h-full bg-[var(--brand-2)] rounded-md" style={{width:`${j.applicants*3}%`}}/>
              </div>
              <div className="w-10 text-sm text-right">{j.applicants}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Billing ----------------
function Billing({ onUpgrade }) {
  return (
    <div className="fade-up">
      <h1 className="font-display text-4xl">Billing</h1>
      <div className="mt-6 card p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm text-[var(--muted)]">Current plan</div>
            <div className="font-display text-2xl mt-1">Pro — Trial</div>
            <div className="text-sm text-[var(--muted)] mt-1">13 days left · ₹14,999/mo after trial</div>
          </div>
          <button onClick={() => onUpgrade(PLANS[2])} className="btn btn-primary">Upgrade to Enterprise</button>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-[var(--paper-2)]"><div className="text-xs text-[var(--muted)]">Credits used</div><div className="font-display text-2xl">3 / 50</div><div className="mt-2"><Progress value={6}/></div></div>
          <div className="p-4 rounded-xl bg-[var(--paper-2)]"><div className="text-xs text-[var(--muted)]">Active jobs</div><div className="font-display text-2xl">4 / 10</div></div>
          <div className="p-4 rounded-xl bg-[var(--paper-2)]"><div className="text-xs text-[var(--muted)]">Team seats</div><div className="font-display text-2xl">2 / 3</div></div>
        </div>
      </div>
      <div className="mt-6 card p-6">
        <h3 className="font-display text-xl mb-4">Invoices</h3>
        <table className="w-full text-sm min-w-[720px]">
          <tbody>
            {[
              {d:"01 Jan 2026", n:"INV-2026-0001", a:"₹0", s:"Trial"},
              {d:"01 Dec 2025", n:"INV-2025-0091", a:"₹14,999", s:"Paid"},
            ].map(r => (
              <tr key={r.n} className="border-t border-[var(--line)]"><td className="py-3">{r.d}</td><td className="py-3 font-mono">{r.n}</td><td className="py-3">{r.a}</td><td className="py-3"><Chip tone={r.s==="Paid"?"mint":"default"}>{r.s}</Chip></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TeamPage() {
  const members = [
    { n: "Priya Sharma", r: "Owner", e: "priya@acmecorp.in" },
    { n: "Kabir Malhotra", r: "Recruiter", e: "kabir@acmecorp.in" },
    { n: "Ananya Rao", r: "Recruiter", e: "ananya@acmecorp.in" },
  ];
  return (
    <div className="fade-up">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">Team</h1>
        <button className="btn btn-accent"><Icon.Plus/> Invite member</button>
      </div>
      <div className="mt-6 card overflow-x-auto">
        {members.map(m => (
          <div key={m.e} className="p-4 flex items-center gap-4 border-b border-[var(--line)] last:border-0">
            <Avatar name={m.n} size={40}/>
            <div className="flex-1"><div className="font-medium">{m.n}</div><div className="text-xs text-[var(--muted)]">{m.e}</div></div>
            <Chip>{m.r}</Chip>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Modals ----------------
function PostJobModal({ onClose }) {
  const [skills, setSkills] = useState(["Python", "FastAPI"]);
  return (
    <Modal onClose={onClose} title="Post a job" testid="post-job-modal">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="text-xs text-[var(--muted)]">Job title *</label>
          <input placeholder="e.g. Senior Backend Engineer" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)]"/>
        </div>
        <div>
          <label className="text-xs text-[var(--muted)]">Location</label>
          <select className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)]">{CITIES.map(c => <option key={c}>{c}</option>)}</select>
        </div>
        <div>
          <label className="text-xs text-[var(--muted)]">Work mode</label>
          <select className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)]"><option>Hybrid</option><option>Remote</option><option>Onsite</option></select>
        </div>
        <div>
          <label className="text-xs text-[var(--muted)]">Experience (yrs)</label>
          <input placeholder="e.g. 3 - 8" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)]"/>
        </div>
        <div>
          <label className="text-xs text-[var(--muted)]">Salary range (LPA)</label>
          <input placeholder="e.g. 22 - 35" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)]"/>
        </div>
        <div className="md:col-span-2 p-4 rounded-xl bg-[var(--brand-3)]/40 border border-[var(--brand-2)]/30">
          <label className="text-xs uppercase tracking-widest text-[var(--brand)]">The critical field</label>
          <div className="mt-1 font-display text-lg">Maximum joining timeline</div>
          <p className="text-xs text-[var(--ink-2)] mt-1">Only candidates who can join within this window will see your job.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[15, 30, 45, 60, 90].map(d => (
              <button key={d} className={`px-4 py-2 rounded-full text-sm font-medium border ${d === 45 ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "bg-white border-[var(--line)]"}`}>≤ {d} days</button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-[var(--muted)]">Required skills</label>
          <div className="mt-1 p-2.5 rounded-xl border border-[var(--line)] bg-white flex flex-wrap gap-1.5">
            {skills.map(s => (
              <Chip key={s} tone="mint"><span className="flex items-center gap-1">{s}<button onClick={() => setSkills(skills.filter(x=>x!==s))} className="opacity-60 hover:opacity-100">×</button></span></Chip>
            ))}
            <input placeholder="Add skill…" className="flex-1 min-w-[100px] outline-none text-sm"
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { setSkills([...skills, e.target.value]); e.target.value = ""; } }}/>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-[var(--muted)]">Description</label>
          <textarea rows={4} placeholder="What will they build?" className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] resize-none"/>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-[var(--muted)]">Screening questions (up to 5)</label>
          <div className="mt-2 space-y-2">
            <input placeholder="What's your current notice period (in days)?" className="w-full px-3 py-2 rounded-xl border border-[var(--line)]"/>
            <input placeholder="Add another question" className="w-full px-3 py-2 rounded-xl border border-[var(--line)] border-dashed"/>
          </div>
        </div>
      </div>
      <div className="mt-6 flex gap-3 justify-end">
        <button onClick={onClose} className="btn btn-ghost">Save draft</button>
        <button data-testid="post-job-publish" onClick={onClose} className="btn btn-primary">Publish job</button>
      </div>
    </Modal>
  );
}

function RazorpayModal({ plan, onClose, onSuccess }) {
  const [step, setStep] = useState("checkout");
  return (
    <Modal onClose={onClose} title="" testid="razorpay-modal" wide={false}>
      {step === "checkout" && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded bg-[#3395FF] text-white grid place-items-center font-bold text-sm">R</div>
            <div className="font-display text-lg">Razorpay <span className="text-xs text-[var(--muted)] font-mono">(test mode)</span></div>
          </div>
          <div className="p-4 rounded-xl bg-[var(--paper-2)]">
            <div className="text-xs text-[var(--muted)]">Order summary</div>
            <div className="flex justify-between mt-1"><span>TalentXpres {plan.name}</span><span className="font-semibold">₹{plan.price.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-sm text-[var(--muted)]"><span>GST 18%</span><span>₹{Math.round(plan.price*0.18).toLocaleString("en-IN")}</span></div>
            <div className="border-t border-[var(--line)] mt-2 pt-2 flex justify-between font-semibold"><span>Total</span><span>₹{Math.round(plan.price*1.18).toLocaleString("en-IN")}</span></div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="text-xs text-[var(--muted)]">Payment method</div>
            {["UPI", "Cards", "Netbanking", "Wallets"].map((m,i) => (
              <label key={m} className={`flex items-center gap-3 p-3 rounded-xl border ${i===0?"border-[var(--brand)] bg-[var(--brand-3)]/30":"border-[var(--line)]"} cursor-pointer`}>
                <input type="radio" name="pm" defaultChecked={i===0}/> {m}
              </label>
            ))}
          </div>
          <button data-testid="razorpay-pay" onClick={() => setStep("success")} className="btn btn-primary w-full mt-4">Pay ₹{Math.round(plan.price*1.18).toLocaleString("en-IN")}</button>
          <p className="text-xs text-[var(--muted)] text-center mt-3">Mocked — no real charge. In production this loads the Razorpay checkout widget.</p>
        </div>
      )}
      {step === "success" && (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-[var(--brand-2)] mx-auto grid place-items-center"><Icon.Check color="white"/></div>
          <h2 className="font-display text-2xl mt-4">Payment successful</h2>
          <p className="text-sm text-[var(--muted)] mt-2">Payment ID: <span className="font-mono">pay_mock_xk8a2</span></p>
          <button onClick={onSuccess} className="btn btn-primary mt-6 w-full">Continue to dashboard</button>
        </div>
      )}
    </Modal>
  );
}

function UnlockModal({ candidate, onClose }) {
  return (
    <Modal onClose={onClose} title="Unlock candidate profile" testid="unlock-modal">
      <div className="flex items-center gap-4">
        <Avatar name={candidate.name} size={64}/>
        <div>
          <div className="font-display text-xl">{candidate.name}</div>
          <div className="text-sm text-[var(--muted)]">{candidate.title} · {candidate.location}</div>
        </div>
      </div>
      <div className="mt-4 p-4 rounded-xl bg-[var(--paper-2)]">
        <div className="text-sm">This uses <b>1 credit</b> from your Pro plan (47 remaining).</div>
        <div className="text-xs text-[var(--muted)] mt-1">{"You'll unlock full contact details, resume, work history and be able to invite them to apply."}</div>
      </div>
      <div className="mt-6 flex gap-3 justify-end">
        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
        <button data-testid="unlock-confirm" onClick={onClose} className="btn btn-primary">Unlock (1 credit)</button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, title, testid, wide = true }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div data-testid={testid} className={`bg-white rounded-2xl w-full ${wide?"max-w-3xl":"max-w-md"} p-6 max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          {title ? <h2 className="font-display text-2xl">{title}</h2> : <span/>}
          <button onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]"><Icon.Close/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
